// BulkCorridorDialog — publish MANY selected scans to the state corridor in
// one flow. For each selected scan:
//   • If the scan already has a location (from the identify flow or a
//     previous Tag Location action), it's used as-is.
//   • Otherwise the user assigns a location per row (ZIP or the phone's
//     current location) or hits the "Apply this ZIP to all still-blank
//     rows" convenience up top.
//
// Submit is disabled until every row has a resolved lat/lon. On confirm we
// hit /api/corridor/bulk-contribute once with the full batch, and pass
// `persist_location=true` for rows that got a NEW location so the Result
// page and the per-scan toggle stay in sync going forward.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MapPin, CheckCircle2, Hash, Leaf } from "lucide-react";
import { toast } from "sonner";

import { corridor as corridorApi } from "@/lib/corridorApi";
import { geocode as geocodeApi } from "@/lib/api";

// Formats a row's scan date compactly for the row header. Uses locale-
// aware short form (e.g. "8/14/25" in en-US, "14/8/25" in es) so identical
// plant names in the same batch always disambiguate. Returns "" when
// neither date_taken nor created_at is present or valid.
function formatRowDate(row) {
  const raw = row?.dateTaken || row?.createdAt;
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString(undefined, { year: "2-digit", month: "numeric", day: "numeric" });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

// Row state:
//   status: "ready" (existing location) | "blank" | "resolving" | "assigned" (new) | "error"
//   lat/lon: numbers when status === ready|assigned
//   fromScan: true if the location came from the scan itself (persist=false)
//   label: friendly string for display (town, or "Current location")
function buildRows(items, scanIds) {
  const idSet = new Set(scanIds || []);
  const list = (items || []).filter((it) => idSet.has(it.scan_id));
  // Preserve the caller's selection order rather than the gallery's sort.
  list.sort((a, b) => scanIds.indexOf(a.scan_id) - scanIds.indexOf(b.scan_id));
  return list.map((it) => {
    const lat = it?.location?.lat;
    const lon = it?.location?.lng;
    const hasLoc = Number.isFinite(lat) && Number.isFinite(lon);
    return {
      scan_id: it.scan_id,
      plant_name: it?.identification?.common_name || it?.common_name || "",
      thumb: it?.thumb_base64 || it?.image_base64 || null,
      // Prefer the user-supplied photo date (date_taken) over the identify
      // timestamp so two duplicates of the same species disambiguate by
      // the meaningful date, not the moment they happened to be uploaded.
      dateTaken: it?.date_taken || null,
      createdAt: it?.created_at || null,
      status: hasLoc ? "ready" : "blank",
      lat: hasLoc ? Number(lat) : null,
      lon: hasLoc ? Number(lon) : null,
      fromScan: hasLoc,
      label: null,
      zipDraft: "",
      error: null,
    };
  });
}

export default function BulkCorridorDialog({ open, onOpenChange, scanIds, allItems, onDone }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [applyAllZip, setApplyAllZip] = useState("");
  const [applyingAll, setApplyingAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(buildRows(allItems, scanIds));
      setApplyAllZip("");
      setApplyingAll(false);
      setSubmitting(false);
    }
  }, [open, allItems, scanIds]);

  const readyCount = useMemo(
    () => rows.filter((r) => r.status === "ready" || r.status === "assigned").length,
    [rows],
  );
  const blankCount = useMemo(
    () => rows.filter((r) => r.status === "blank" || r.status === "error").length,
    [rows],
  );
  const allReady = rows.length > 0 && rows.every((r) => r.status === "ready" || r.status === "assigned");

  const patchRow = (scan_id, patch) => {
    setRows((prev) => prev.map((r) => (r.scan_id === scan_id ? { ...r, ...patch } : r)));
  };

  const resolveZipFor = async (scan_id, zip) => {
    patchRow(scan_id, { status: "resolving", error: null });
    try {
      const { data } = await geocodeApi.zip(zip);
      patchRow(scan_id, {
        status: "assigned",
        lat: Number(data.lat),
        lon: Number(data.lng),
        label: data.label || `US ${zip}`,
        fromScan: false,
        zipDraft: "",
        error: null,
      });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 404 ? t("result.zipNotFound") :
        status === 422 ? t("result.zipInvalid") :
        t("bulkCorridor.zipFail");
      patchRow(scan_id, { status: "blank", error: msg });
    }
  };

  const assignGpsFor = async (scan_id) => {
    if (!navigator.geolocation) {
      toast.error(t("result.locationUnsupported"));
      return;
    }
    patchRow(scan_id, { status: "resolving", error: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        patchRow(scan_id, {
          status: "assigned",
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          label: t("bulkCorridor.currentLocationLabel"),
          fromScan: false,
          error: null,
        });
      },
      () => patchRow(scan_id, { status: "blank", error: t("result.locationDenied") }),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
    );
  };

  const applyZipToAllBlanks = async () => {
    const zip = applyAllZip.trim();
    if (!/^\d{5}$/.test(zip)) {
      toast.error(t("result.zipInvalid"));
      return;
    }
    const blanks = rows.filter((r) => r.status === "blank" || r.status === "error");
    if (blanks.length === 0) return;
    setApplyingAll(true);
    try {
      // Resolve once, then propagate to every still-blank row.
      const { data } = await geocodeApi.zip(zip);
      const stamp = {
        status: "assigned",
        lat: Number(data.lat),
        lon: Number(data.lng),
        label: data.label || `US ${zip}`,
        fromScan: false,
        zipDraft: "",
        error: null,
      };
      setRows((prev) => prev.map((r) => {
        if (r.status === "blank" || r.status === "error") return { ...r, ...stamp };
        return r;
      }));
      setApplyAllZip("");
      toast.success(t("bulkCorridor.appliedToBlanks", { count: blanks.length }));
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        status === 404 ? t("result.zipNotFound") :
        status === 422 ? t("result.zipInvalid") :
        t("bulkCorridor.zipFail")
      );
    } finally {
      setApplyingAll(false);
    }
  };

  const assignGpsToAllBlanks = () => {
    if (!navigator.geolocation) {
      toast.error(t("result.locationUnsupported"));
      return;
    }
    const blanks = rows.filter((r) => r.status === "blank" || r.status === "error");
    if (blanks.length === 0) return;
    // Grab GPS once and stamp every still-blank row — one permission prompt,
    // one geolocation call, N rows filled.
    setApplyingAll(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const stamp = {
          status: "assigned",
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          label: t("bulkCorridor.currentLocationLabel"),
          fromScan: false,
          error: null,
        };
        setRows((prev) => prev.map((r) => {
          if (r.status === "blank" || r.status === "error") return { ...r, ...stamp };
          return r;
        }));
        toast.success(t("bulkCorridor.appliedToBlanks", { count: blanks.length }));
        setApplyingAll(false);
      },
      () => {
        toast.error(t("result.locationDenied"));
        setApplyingAll(false);
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 },
    );
  };

  const submit = async () => {
    if (!allReady) return;
    setSubmitting(true);
    try {
      const contributions = rows
        .filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon))
        .map((r) => ({
          scan_id: r.scan_id,
          lat: r.lat,
          lon: r.lon,
          // Only persist newly-assigned locations onto the scan doc; leave
          // pre-existing scan locations exactly as the user set them.
          persist_location: !r.fromScan,
        }));
      const { data } = await corridorApi.bulkContribute(contributions);
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bulkCorridor.submitFail"));
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || applyingAll;
  const closeIfIdle = (o) => { if (!busy) onOpenChange(o); };

  return (
    <Dialog open={open} onOpenChange={closeIfIdle}>
      <DialogContent className="max-w-lg" data-testid="bulk-corridor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {t("bulkCorridor.title", { count: rows.length })}
          </DialogTitle>
          <DialogDescription>
            {blankCount === 0
              ? t("bulkCorridor.descAllReady")
              : t("bulkCorridor.desc", { blanks: blankCount, total: rows.length, count: rows.length })}
          </DialogDescription>
        </DialogHeader>

        {/* Convenience row — apply a single location to every still-blank
            row at once. Hidden when there's nothing to fill. */}
        {blankCount > 0 && (
          <div
            className="rounded-2xl border border-[#E8E5DF] bg-cream p-3"
            data-testid="bulk-corridor-apply-all"
          >
            <p className="text-[11px] uppercase tracking-[0.14em] text-leaf-mist mb-2">
              {t("bulkCorridor.applyAllLabel", { count: blankCount })}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
              <div className="flex gap-2 flex-1 min-w-0">
                <input
                  type="text"
                  inputMode="numeric"
                  value={applyAllZip}
                  onChange={(e) => setApplyAllZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder={t("result.zipPlaceholder")}
                  maxLength={5}
                  disabled={busy}
                  data-testid="bulk-corridor-apply-all-zip"
                  aria-label={t("result.zipInputAria")}
                  className="flex-1 rounded-2xl border border-[#E8E5DF] bg-white px-3 py-2 text-sm text-leaf-ink focus:outline-none focus:ring-2 focus:ring-leaf/40 tabular-nums"
                />
                <button
                  type="button"
                  onClick={applyZipToAllBlanks}
                  disabled={busy || !/^\d{5}$/.test(applyAllZip)}
                  data-testid="bulk-corridor-apply-all-submit"
                  className="shrink-0 whitespace-nowrap rounded-full px-4 py-2 bg-leaf text-white text-xs font-medium hover:bg-leaf-deep transition inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {applyingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : null}
                  {t("bulkCorridor.applyAllZipButton")}
                </button>
              </div>
              <button
                type="button"
                onClick={assignGpsToAllBlanks}
                disabled={busy}
                data-testid="bulk-corridor-apply-all-gps"
                className="shrink-0 whitespace-nowrap rounded-full px-3 py-2 bg-white border border-[#E8E5DF] text-leaf-ink text-xs hover:bg-parchment transition inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("bulkCorridor.applyAllGpsButton")}
              </button>
            </div>
          </div>
        )}

        {/* Per-row editor. Fixed max height so a large selection doesn't
            push the submit button below the fold. */}
        <ul
          className="max-h-80 overflow-y-auto pr-1 -mx-1 px-1 flex flex-col gap-2"
          data-testid="bulk-corridor-rows"
        >
          {rows.map((r) => (
            <BulkCorridorRow
              key={r.scan_id}
              row={r}
              busy={busy}
              onZipDraftChange={(v) => patchRow(r.scan_id, { zipDraft: v })}
              onZipSubmit={() => resolveZipFor(r.scan_id, r.zipDraft)}
              onUseGps={() => assignGpsFor(r.scan_id)}
              onClear={() => patchRow(r.scan_id, {
                status: "blank",
                lat: null,
                lon: null,
                label: null,
                fromScan: false,
              })}
            />
          ))}
        </ul>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="bulk-corridor-cancel"
            className="rounded-full px-5 py-2.5 text-sm bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!allReady || busy}
            data-testid="bulk-corridor-submit"
            className="rounded-full px-5 py-2.5 text-sm bg-leaf text-white hover:bg-leaf-deep transition inline-flex items-center gap-2 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Leaf className="w-4 h-4" strokeWidth={1.5} />}
            {t("bulkCorridor.submit", { count: readyCount })}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkCorridorRow({ row, busy, onZipDraftChange, onZipSubmit, onUseGps, onClear }) {
  const { t } = useTranslation();
  const ready = row.status === "ready" || row.status === "assigned";
  const resolving = row.status === "resolving";
  return (
    <li
      className={`rounded-2xl border p-3 transition ${
        ready ? "border-leaf/30 bg-leaf/[0.04]" : "border-[#E8E5DF] bg-white"
      }`}
      data-testid={`bulk-corridor-row-${row.scan_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-xl overflow-hidden bg-parchment flex items-center justify-center">
          {row.thumb ? (
            <img src={row.thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            <Leaf className="w-4 h-4 text-leaf-mist" strokeWidth={1.5} aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-medium text-leaf-ink truncate">
              {row.plant_name || t("bulkCorridor.unknownPlant")}
            </p>
            {/* Scan date next to the name — indispensable when a user has
                multiple scans of the same species (a common case for
                garden diarists). Prefers the user's manually-set photo
                date; falls back to the identify timestamp. Rendered in
                the locale-formatted "short" form so we don't blow the
                row layout at high text-size. */}
            {formatRowDate(row) && (
              <span
                className="shrink-0 text-[11px] text-leaf-mist tabular-nums"
                data-testid={`bulk-corridor-row-date-${row.scan_id}`}
                aria-label={t("bulkCorridor.rowDateAria", { date: formatRowDate(row) })}
              >
                {formatRowDate(row)}
              </span>
            )}
          </div>
          {ready ? (
            <p className="mt-0.5 text-[12px] text-leaf-mist inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-leaf shrink-0" strokeWidth={1.75} />
              <span className="truncate">
                {row.label ||
                  (row.fromScan
                    ? t("bulkCorridor.usingScanLocation")
                    : `${row.lat.toFixed(3)}, ${row.lon.toFixed(3)}`)}
              </span>
            </p>
          ) : (
            <p className="mt-0.5 text-[12px] text-rust">
              {row.error || t("bulkCorridor.needsLocation")}
            </p>
          )}
        </div>
        {ready && (
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            data-testid={`bulk-corridor-row-clear-${row.scan_id}`}
            className="text-[11px] underline underline-offset-4 decoration-leaf/50 hover:decoration-leaf text-leaf-ink shrink-0 disabled:opacity-50"
          >
            {t("bulkCorridor.change")}
          </button>
        )}
      </div>

      {!ready && !resolving && (
        <div className="mt-2 flex gap-2 items-stretch pl-13">
          <div className="flex-1 flex gap-1.5 min-w-0">
            <div className="flex items-center px-2 rounded-l-full bg-parchment text-leaf-mist">
              <Hash className="w-3.5 h-3.5" strokeWidth={1.75} aria-hidden />
            </div>
            <input
              type="text"
              inputMode="numeric"
              value={row.zipDraft}
              onChange={(e) => onZipDraftChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && /^\d{5}$/.test(row.zipDraft)) {
                  e.preventDefault();
                  onZipSubmit();
                }
              }}
              placeholder={t("result.zipPlaceholder")}
              maxLength={5}
              disabled={busy}
              data-testid={`bulk-corridor-row-zip-${row.scan_id}`}
              aria-label={t("result.zipInputAria")}
              className="flex-1 min-w-0 rounded-r-full border border-[#E8E5DF] bg-cream px-3 py-1.5 text-sm text-leaf-ink focus:outline-none focus:ring-2 focus:ring-leaf/40 tabular-nums"
            />
            <button
              type="button"
              onClick={onZipSubmit}
              disabled={busy || !/^\d{5}$/.test(row.zipDraft)}
              data-testid={`bulk-corridor-row-zip-submit-${row.scan_id}`}
              className="rounded-full px-3 py-1.5 bg-leaf text-white text-xs hover:bg-leaf-deep transition disabled:opacity-60"
            >
              {t("result.zipSubmit")}
            </button>
          </div>
          <button
            type="button"
            onClick={onUseGps}
            disabled={busy}
            data-testid={`bulk-corridor-row-gps-${row.scan_id}`}
            className="shrink-0 rounded-full px-3 py-1.5 bg-white border border-[#E8E5DF] text-leaf-ink text-xs hover:bg-parchment transition inline-flex items-center gap-1.5 disabled:opacity-60"
            aria-label={t("bulkCorridor.rowGpsAria")}
          >
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      )}
      {resolving && (
        <div className="mt-2 text-[12px] text-leaf-mist inline-flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.75} aria-hidden />
          {t("bulkCorridor.resolving")}
        </div>
      )}
    </li>
  );
}
